import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge, Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Input';
import { Dialog } from '@/components/ui/Dialog';
import { useFormFills } from '@/hooks/useDispatcher';
import { formatDateTime, truncate } from '@/lib/utils';
import type { FormFill } from '@/types/api';

export default function FormsPage() {
  const [status, setStatus] = useState<string>('');
  const { data, isLoading } = useFormFills({ status: status || undefined });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [htmlPreview, setHtmlPreview] = useState<string | null>(null);

  return (
    <div>
      <PageHeader
        title="Form-Fills"
        description="Tentatives de remplissage de formulaires de contact"
        action={
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-44">
            <option value="">Tous statuts</option>
            <option value="pending">pending</option>
            <option value="running">running</option>
            <option value="done">done</option>
            <option value="failed">failed</option>
            <option value="multistep_pending">multistep_pending</option>
          </Select>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : !data?.length ? (
        <Card>
          <EmptyState icon={FileText} title="Aucun form-fill enregistré" />
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.map((f) => (
            <FormFillCard
              key={f.id}
              fill={f}
              expanded={expanded === f.id}
              onToggle={() => setExpanded(expanded === f.id ? null : f.id)}
              onShowHtml={() => setHtmlPreview(f.remaining_html ?? null)}
            />
          ))}
        </div>
      )}

      <Dialog
        open={!!htmlPreview}
        onClose={() => setHtmlPreview(null)}
        title="HTML restant (multi-step)"
        size="lg"
      >
        <pre className="text-xs text-gray-700 bg-gray-50 border border-brand-border rounded-brand p-3 overflow-x-auto">
          {htmlPreview}
        </pre>
      </Dialog>
    </div>
  );
}

interface CardProps {
  fill: FormFill;
  expanded: boolean;
  onToggle: () => void;
  onShowHtml: () => void;
}

function FormFillCard({ fill, expanded, onToggle, onShowHtml }: CardProps) {
  return (
    <Card className="flex flex-col">
      <CardHeader
        title={
          <Link to={`/contacts/${fill.contact_id}`} className="hover:underline">
            {truncate(fill.form_url, 56)}
          </Link>
        }
        subtitle={`Tentative ${fill.attempt_no} · ${formatDateTime(fill.created_at)}`}
        action={<StatusBadge status={fill.status} />}
      />
      {fill.screenshot_url && (
        <img
          src={fill.screenshot_url}
          alt="screenshot"
          className="w-full max-h-48 object-cover object-top border-b border-brand-border"
        />
      )}
      <div className="px-5 py-3 flex items-center gap-2 flex-wrap text-xs">
        <Button size="sm" variant="ghost" onClick={onToggle}>
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          Logs
        </Button>
        <Button size="sm" variant="secondary" disabled>
          Replay (V2)
        </Button>
        {fill.multistep_pending && (
          <>
            <Badge tone="yellow">multistep</Badge>
            <Button size="sm" variant="ghost" onClick={onShowHtml}>
              Voir HTML restant
            </Button>
          </>
        )}
      </div>
      {expanded && (
        <pre className="mx-5 mb-5 bg-gray-50 border border-brand-border rounded-brand px-3 py-2 text-[11px] text-gray-700 overflow-x-auto">
          {JSON.stringify(fill.execution_logs ?? {}, null, 2)}
        </pre>
      )}
    </Card>
  );
}
